/*! Copyright (c) 2026, XAPPmedia */
import { useEffect } from "react";

/**
 * Body attribute a host page sets to declare "a form widget already *is* this page".
 *
 * The standalone contact/booking page (`@xapp/contact-app`, serving contact.xapp.ai,
 * book.xapp.ai and sche.dule.pro) renders a docked form as its whole content, then injects
 * the chat widget on top when the URL carries `?chatWidgetKey=`. That widget arrives with
 * the App's Studio-configured ActionBar, which offers a Contact button and a Book button
 * into the very form already filling the screen.
 *
 * The page has to say so, because nothing the widget can observe would tell it. A docked
 * form in the DOM is not the same claim - plenty of pages embed one *and* want a separate
 * one from the bar.
 *
 * The reader is `@xapp/chat-widget`'s `useIsFormPage`, and its stylesheet also keys the
 * form-widget side button off this. Those packages ship separately, so this string is the
 * contract between them - the same arrangement `[data-xapp-form-side-button]` already uses.
 * Do not rename it on one side only. XappMedia/chat-widget#1626.
 */
export const FORM_PAGE_ATTRIBUTE = "data-xapp-form-page";

/**
 * Companion reference count, so two markers on one page do not clear each other on the way
 * out. Mirrors the `data-xapp-action-bar-count` the chat widget keeps for the same reason.
 */
export const FORM_PAGE_COUNT_ATTRIBUTE = "data-xapp-form-page-count";

function readCount(): number {
    const raw = parseInt(document.body.getAttribute(FORM_PAGE_COUNT_ATTRIBUTE) || "0", 10);
    return Number.isNaN(raw) ? 0 : raw;
}

/**
 * Declares this page to be a form page, and returns the release for it.
 *
 * Imperative form of {@link useFormPageMarker}, for pages that are not React.
 */
export function markFormPage(): () => void {
    if (typeof document === "undefined" || !document.body) {
        return () => undefined;
    }

    document.body.setAttribute(FORM_PAGE_COUNT_ATTRIBUTE, String(readCount() + 1));
    document.body.setAttribute(FORM_PAGE_ATTRIBUTE, "true");

    let released = false;
    return () => {
        // Idempotent: a double release must not drop the count below what other markers hold.
        if (released) {
            return;
        }
        released = true;

        const remaining = Math.max(0, readCount() - 1);
        if (remaining === 0) {
            document.body.removeAttribute(FORM_PAGE_ATTRIBUTE);
            document.body.removeAttribute(FORM_PAGE_COUNT_ATTRIBUTE);
        } else {
            document.body.setAttribute(FORM_PAGE_COUNT_ATTRIBUTE, String(remaining));
        }
    };
}

/**
 * Declares this page to be a form page for as long as the component is mounted.
 *
 * @param enabled pass false to render without making the claim
 */
export function useFormPageMarker(enabled = true): void {
    useEffect(() => {
        if (!enabled) {
            return undefined;
        }
        return markFormPage();
    }, [enabled]);
}
